import { Box, Stack, Backdrop } from "@mui/material";
import {
  DataGrid,
  GridColumnMenuContainer,
  GridColumnMenuProps,
  GridColumnMenuColumnsItem,
  GridColumnMenuFilterItem,
  DataGridProps
} from "@mui/x-data-grid";
import { ComponentType, ReactNode, useState, useMemo, useRef } from "react";
import CustomPagination from "./CustomPagination";

export interface CustomDataGridProps extends DataGridProps {
  footerContent?: ReactNode;
}

export interface CustomDataGridFooterProps {
  footerContent?: ReactNode;
  hideFooterPagination?: boolean;
  PaginationComponent: ComponentType;
}

export function CustomDataGridFooter({ footerContent, hideFooterPagination, PaginationComponent }: CustomDataGridFooterProps) {
  return (
    <Stack
      direction='row'
      spacing={32}
      padding='0.5rem 0.75rem'
      alignItems='center'
      justifyContent={!footerContent ? "flex-end" : "space-between"}
      sx={{ width: "100%", borderTop: "1px solid", borderColor: "grey.300" }}
    >
      {footerContent}
      {!hideFooterPagination && <PaginationComponent />}
    </Stack>
  );
}

export function CustomColumnMenu(props: GridColumnMenuProps) {
  const { hideMenu, colDef, open } = props;

  return (
    <Box
      sx={{
        "& .MuiMenuItem-root": {
          padding: "0.5rem 0.75rem",
          gap: "0.5rem"
        },
        "& .MuiListItemIcon-root svg": {
          fontSize: "1.5rem"
        },
        "& .MuiListItemText-root": {
          fontSize: "0.875rem",
          color: "grey.700"
        }
      }}
    >
      <GridColumnMenuContainer open={open} hideMenu={hideMenu} colDef={colDef}>
        <GridColumnMenuFilterItem onClick={hideMenu} colDef={colDef} />
        <GridColumnMenuColumnsItem colDef={colDef} onClick={hideMenu} />
      </GridColumnMenuContainer>
    </Box>
  );
}

function CustomDataGrid({
  slots,
  onColumnVisibilityModelChange,
  onStateChange,
  slotProps,
  onPaginationModelChange,
  paginationModel,
  sx,
  footerContent,
  hideFooterPagination,
  ...rest
}: CustomDataGridProps) {
  const [activePanel, setActivePanel] = useState<"columns" | "filters" | null>(null);
  const isColumnPanelOpen = activePanel === "columns";
  const lastColumnFieldRef = useRef<string | null>(null);
  const clearPanelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mergedSlotProps = useMemo<any>(
    () => ({
      // loading skeleton
      loadingOverlay: {
        variant: "skeleton",
        noRowsVariant: "skeleton",
        ...(slotProps?.loadingOverlay || {})
      },
      footer: {
        footerContent: footerContent,
        hideFooterPagination: hideFooterPagination,
        PaginationComponent: CustomPagination
      },
      // Panel positioning: anchor filter panel to specific column header
      panel: {
        ...(slotProps?.panel || {}),
        ...(activePanel === "filters" && lastColumnFieldRef.current
          ? {
              anchorEl: () => {
                const el = document.querySelector(
                  `.MuiDataGrid-columnHeader[data-field="${lastColumnFieldRef.current}"]`
                ) as HTMLElement | null;
                return el;
              },
              placement: "bottom-end" as const
            }
          : {})
      },
      // column filter / visibility panel
      columnsPanel: {
        ...(slotProps?.columnsPanel || {}),
        sx: {
          backgroundColor: "#fff",
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          minWidth: "24rem",
          padding: "1.5rem",
          gap: "0.5rem",
          borderRadius: "0.5rem",
          border: "1px solid",
          borderColor: "grey.200",
          boxShadow: "0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)",
          zIndex: 99999,

          ".MuiPaper-root": {
            display: "none!important"
          },

          "& .MuiDataGrid-columnsManagementRow": {
            margin: 0,
            gap: "0.62rem",
            paddingBlock: "0.25rem",
            "& .MuiButtonBase-root": { padding: 0 },
            "& .MuiFormControlLabel-label": {
              fontSize: "0.8125rem",
              color: "grey.600"
            },
            "& .MuiSvgIcon-root": {
              height: "1.25rem",
              width: "1.25rem",
              color: "grey.600"
            }
          },

          "& .MuiDataGrid-columnsManagementFooter": {
            "& .MuiSvgIcon-root": { color: "grey.600" },
            "& .MuiFormControlLabel-root": {
              margin: 0,
              gap: "0.5rem",
              '& ."MuiTypography-root': {
                fontSize: "0.8125rem",
                color: "grey.600"
              }
            },
            "& .MuiButtonBase-root": { color: "grey.600" }
          },

          ...(slotProps?.columnsPanel?.sx || {})
        }
      },

      filterPanel: {
        ...(slotProps?.filterPanel || {}),
        sx: {
          overflow: "visible",
          backgroundColor: "#fff",
          padding: "0",

          // The filter form row
          "& .MuiDataGrid-filterForm": {
            display: "flex",
            flexDirection: "row",
            gap: "0.75rem",
            padding: "1rem 2.5rem 1rem 1rem",
            alignItems: "flex-end",
            overflow: "visible",
            position: "relative",

            // Hide default logic operator column (And/Or) for single filter
            "& .MuiDataGrid-filterFormLogicOperatorInput": {
              display: "none"
            }
          },

          // Column input section
          "& .MuiDataGrid-filterFormColumnInput": {
            minWidth: "10rem",
            "& .MuiInputLabel-root": {
              position: "relative",
              transform: "none",
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "#344054",
              marginBottom: "0.125rem"
            },
            "& .MuiInputBase-root": {
              borderRadius: "0.5rem",
              border: "1px solid",
              borderColor: "#D0D5DD",
              height: "2.5rem",
              fontSize: "0.875rem",
              color: "#344054",
              padding: "0 0.75rem",
              marginTop: "0.25rem",
              "&:hover": { borderColor: "#98A2B3" },
              "&.Mui-focused": { borderColor: "#1570EF", boxShadow: "0 0 0 2px rgba(21, 112, 239, 0.15)" },
              "&::before, &::after": { display: "none" }
            },
            "& .MuiSelect-icon": { color: "#667085" }
          },

          // Operator input section
          "& .MuiDataGrid-filterFormOperatorInput": {
            minWidth: "10rem",
            "& .MuiInputLabel-root": {
              position: "relative",
              transform: "none",
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "#344054",
              marginBottom: "0.125rem"
            },
            "& .MuiInputBase-root": {
              borderRadius: "0.5rem",
              border: "1px solid",
              borderColor: "#D0D5DD",
              height: "2.5rem",
              fontSize: "0.875rem",
              color: "#344054",
              padding: "0 0.75rem",
              marginTop: "0.25rem",
              "&:hover": { borderColor: "#98A2B3" },
              "&.Mui-focused": { borderColor: "#1570EF", boxShadow: "0 0 0 2px rgba(21, 112, 239, 0.15)" },
              "&::before, &::after": { display: "none" }
            },
            "& .MuiSelect-icon": { color: "#667085" }
          },

          // Value input section
          "& .MuiDataGrid-filterFormValueInput": {
            minWidth: "10rem",
            "& .MuiInputLabel-root": {
              position: "relative",
              transform: "none",
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "#344054",
              marginBottom: "0.125rem"
            },
            "& .MuiInputBase-root": {
              borderRadius: "0.5rem",
              border: "1px solid",
              borderColor: "#D0D5DD",
              height: "2.5rem",
              fontSize: "0.875rem",
              color: "#344054",
              padding: "0 0.75rem",
              marginTop: "0.25rem",
              "&:hover": { borderColor: "#98A2B3" },
              "&.Mui-focused": { borderColor: "#1570EF", boxShadow: "0 0 0 2px rgba(21, 112, 239, 0.15)" },
              "&::before, &::after": { display: "none" },
              "& .MuiInputBase-input::placeholder": {
                color: "#98A2B3",
                opacity: 1
              }
            }
          },

          // Delete / close icon - top right
          "& .MuiDataGrid-filterFormDeleteIcon": {
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            "& .MuiIconButton-root": {
              padding: "0.25rem",
              color: "#667085",
              "&:hover": { color: "#344054", backgroundColor: "#F2F4F7" }
            }
          },

          // Paper wrapper
          "& .MuiPaper-root": {
            borderRadius: "0.5rem",
            border: "1px solid",
            borderColor: "#EAECF0",
            boxShadow: "0 4px 6px -2px rgba(16, 24, 40, 0.03), 0 12px 16px -4px rgba(16, 24, 40, 0.08)",
            overflow: "visible"
          },
          ...(slotProps?.filterPanel?.sx || {})
        }
      },

      basePopper: {
        ...(slotProps?.basePopper || {}),
        disablePortal: false,
        sx: {
          background: "transparent",
          borderRadius: "0.5rem",
          zIndex: 99999,
          overflow: "visible",
          // Only center if this Popper is actually the preference panel (Columns/Filters)
          // This prevents targeting the column menu if it's still closing during transition
          "&.MuiDataGrid-panel": {
            ...(isColumnPanelOpen && {
              position: "fixed!important",
              top: "50%!important",
              left: "50%!important",
              transform: "translate(-50%, -50%)!important",
              "& .MuiPaper-root": {
                border: "0",
                boxShadow: "none"
              }
            })
          },
          ...(slotProps?.basePopper?.sx || {})
        }
      },

      columnsManagement: {
        ...(slotProps?.columnsManagement || {}),
        searchInputProps: {
          placeholder: "Click to search here",
          ...(slotProps?.columnsManagement?.searchInputProps || {}),
          sx: {
            "& .MuiInputBase-root": {
              height: "2.5rem",
              padding: "0.5rem 0.72919rem",
              borderRadius: "0.375rem",
              border: "1px solid",
              borderColor: "grey.300",
              gap: "0.38rem",

              "& .MuiInputAdornment-root": {
                margin: 0,
                "& svg": {
                  width: "1.5rem",
                  height: "1.5rem"
                }
              }
            },
            ...(slotProps?.columnsManagement?.searchInputProps?.sx || {})
          }
        }
      },
      // Ensure filter form Select dropdown menus render above the panel Popper (z-index 99999)
      baseSelect: {
        ...(slotProps?.baseSelect || {}),
        MenuProps: {
          ...(slotProps?.baseSelect?.MenuProps || {}),
          sx: {
            zIndex: 100001,
            "& .MuiPaper-root": {
              borderRadius: "0.5rem",
              border: "1px solid",
              borderColor: "#EAECF0",
              boxShadow: "0 4px 6px -2px rgba(16, 24, 40, 0.03), 0 12px 16px -4px rgba(16, 24, 40, 0.08)",
              marginTop: "0.25rem"
            },
            "& .MuiMenuItem-root": {
              fontSize: "0.875rem",
              color: "#344054",
              padding: "0.5rem 0.875rem",
              "&:hover": { backgroundColor: "#F9FAFB" },
              "&.Mui-selected": {
                backgroundColor: "#F2F4F7",
                "&:hover": { backgroundColor: "#EAECF0" }
              }
            },
            ...(slotProps?.baseSelect?.MenuProps?.sx || {})
          }
        }
      },
      // finally allow full override
      ...(slotProps || {})
    }),
    [slotProps, footerContent, hideFooterPagination, isColumnPanelOpen, activePanel]
  );

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      <DataGrid
        pagination
        paginationModel={paginationModel}
        initialState={{
          pagination: {}
        }}
        slots={{
          footer: CustomDataGridFooter as any,
          columnMenu: CustomColumnMenu,
          ...slots
        }}
        onColumnVisibilityModelChange={(modal, details) => {
          // Let onStateChange be the driver for current panel to avoid flashes
          onColumnVisibilityModelChange && onColumnVisibilityModelChange(modal, details);
        }}
        onStateChange={(state, event, details) => {
          // Track which column's menu was last open
          if ((state as any).columnMenu?.open && (state as any).columnMenu?.field) {
            lastColumnFieldRef.current = (state as any).columnMenu.field;
          }

          if (state.preferencePanel.open) {
            if (clearPanelTimerRef.current) {
              clearTimeout(clearPanelTimerRef.current);
              clearPanelTimerRef.current = null;
            }
            const nextPanel = (state.preferencePanel.openedPanelValue as any) || "columns";
            setActivePanel((prev) => (prev === nextPanel ? prev : nextPanel));
          } else {
            // Wait slightly before clearing to allow the re-open event to cancel it
            if (!clearPanelTimerRef.current) {
              clearPanelTimerRef.current = setTimeout(() => {
                setActivePanel(null);
                clearPanelTimerRef.current = null;
              }, 50);
            }
          }
          onStateChange && onStateChange(state, event, details);
        }}
        onPaginationModelChange={onPaginationModelChange}
        slotProps={mergedSlotProps}
        sx={{
          overflowX: "auto",
          "& .MuiDataGrid-columnHeader": {
            fontSize: "0.875rem",
            backgroundColor: "grey.700",
            color: "grey.50",
            "& > *": {
              background: "transparent!important",
              color: "grey.50"
            },
            "& .MuiIconButton-root": {
              color: "grey.50"
            }
          },
          // stripe background for row
          ".MuiDataGrid-row:nth-child(even)": {
            backgroundColor: "#FAFAFA"
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            fontSize: "0.875rem",
            color: "#101828",
            display: "inline-flex",
            alignItems: "center",
            overflow: "hidden",
            "& > *": {
              fontSize: "0.875rem",
              color: "#101828",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "action.hover"
          },
          "& .MuiDataGrid-main": {
            minHeight: "auto"
          },
          ...sx
        }}
        {...rest}
      />
      <Backdrop
        sx={{
          zIndex: 99998,
          backgroundColor: "rgba(11, 19, 32, 0.73)"
        }}
        open={isColumnPanelOpen}
        transitionDuration={0}
      />
    </Box>
  );
}

export default CustomDataGrid;
